<template>
    <v-card>
        <v-card-title class="text-h5">Gym Champs Score Flash</v-card-title>

        <v-card-text>
            <v-row align="center" class="mx-0 mt-2">
                <v-col class="mb-0 py-0">
                    Events
                    <div class="d-flex flex-row">
                        <v-checkbox v-model="selectedEvents.TI" label="Trampoline" class="mr-2" />
                        <v-checkbox v-model="selectedEvents.DM" label="Double Mini" class="mx-2" />
                        <v-checkbox v-model="selectedEvents.TU" label="Tumbling" class="mx-2" />
                    </div>
                </v-col>
            </v-row>

            <v-row align="center" class="mx-0 my-0">
                <v-col class="py-0">
                    <v-switch
                        v-model="ribbonStyle"
                        true-value="single"
                        false-value="double"
                        :label="`Ribbon style: ${ribbonStyle}`"
                    />
                    <v-switch
                        v-if="ribbonStyle === 'double'"
                        v-model="side"
                        true-value="right"
                        false-value="left"
                        :label="`Side: ${side}`"
                    />
                </v-col>

                <v-col>
                    <div
                        :class="['image-crop', ribbonStyle, side]"
                        :style="{
                            'background-image': `url(${$page.props.cdn}/graphics/usagym-champs-ribbon.png)`,
                            'max-width': '240px',
                            height: ribbonStyle === 'double' ? '130px' : '42px',
                            border: '5px solid white',
                        }"
                    ></div>
                </v-col>
            </v-row>

            <v-row align="center" class="mx-0 mt-2 mb-2">
                <v-col>
                    <v-switch v-model="testing" label="Testing" />
                </v-col>
                <v-col v-if="testing" class="py-0">
                    <v-text-field v-model="resultCategoryId" label="Result category (optional):" />
                </v-col>
            </v-row>

            <v-row align="center" class="mx-0 mt-2 mb-2">
                <v-col class="py-0">
                    <v-checkbox v-model="viewOnly" label="View only (Don't mark flashed)" />
                </v-col>
            </v-row>

            <v-row align="center" class="mx-0 mt-2">
                <v-col class="py-0">
                    Min time shown (seconds)
                    <v-slider
                        v-model="minTime"
                        class="align-center"
                        step="5"
                        :max="600"
                        :min="5"
                        hide-details
                        prepend-icon="mdi-clock"
                    >
                        <template #append>
                            <v-text-field v-model="minTime" class="mt-0 pt-0" type="number" style="width: 60px" />
                        </template>
                    </v-slider>
                </v-col>

                <v-col class="py-0">
                    Max time shown (seconds)
                    <v-slider
                        v-model="maxTime"
                        class="align-center"
                        step="5"
                        :max="600"
                        :min="5"
                        hide-details
                        prepend-icon="mdi-clock"
                    >
                        <template #append>
                            <v-text-field v-model="maxTime" class="mt-0 pt-0" type="number" style="width: 60px" />
                        </template>
                    </v-slider>
                </v-col>
            </v-row>
        </v-card-text>

        <v-card-actions>
            <v-spacer />
            <v-btn color="primary" @click="present"> Present </v-btn>
        </v-card-actions>
    </v-card>
</template>

<script>
import Meet from '@/Models/Meet.js';
import backgroundColors from '@/background-colors.js';

export default {
    name: 'GymChampsScoreFlashModal',

    props: {
        meet: {
            type: Meet,
            required: true,
        },
    },

    data: () => ({
        selectedSession: null,
        loadingSessions: false,
        error: null,
        selectedEvents: {
            TI: true,
            DM: false,
            TU: false,
        },
        minTime: 45,
        maxTime: 60 * 8,
        ribbonStyle: 'single',
        side: 'left',
        contrast: 'dark',
        equipment: null,
        viewOnly: false,
        testing: false,
        resultCategoryId: null,
    }),

    methods: {
        present() {
            const backgroundType = this.contrast === null ? 'gradient' : 'color';

            const dark = this.contrast !== 'light' ? 'true' : 'false';

            let url = `/meets/${this.meet.id}/score-flash/gym-champs?dark=${dark}&background_type=${backgroundType}`;

            url += `&min_time=${this.minTime}&max_time=${this.maxTime}`;

            if (this.viewOnly) {
                url += '&should_mark_flashed=false';
            }

            const events = [];

            Object.keys(this.selectedEvents).forEach(eventKey => {
                if (this.selectedEvents[eventKey]) {
                    events.push(eventKey);
                }
            });

            url += `&events=${events.join(',')}`;

            if (this.testing) {
                url += '&testing=1';

                if (this.resultCategoryId) {
                    url += `&result_category_id=${this.resultCategoryId}`;
                }
            }

            url += `&ribbon_style=${this.ribbonStyle}`;

            if (this.ribbonStyle === 'double') {
                url += `&side=${this.side}`;
            }

            if (this.contrast === 'dark') {
                url += `&background_colors[]=${encodeURIComponent('#000000,#000000')}`;
                url += '&color=ffffff';
            } else if (this.contrast === 'light') {
                url += `&background_colors[]=${encodeURIComponent('#ffffff,#ffffff')}`;
                url += '&color=000000';
            } else {
                backgroundColors.forEach(([startColor, endColor]) => {
                    url += `&background_colors[]=${encodeURIComponent(`${startColor},${endColor}`)}`;
                });
            }

            window.open(url, '_blank');
        },
    },
};
</script>

<style lang="scss">
.image-crop {
    height: 256px;
    background-repeat: no-repeat;
}

.image-crop.single {
    background-size: contain;
    background-position: center;
    width: 240px;
}

.image-crop.double.left {
    background-position: left top;
    width: 240px;
    background-size: 960px auto;
}

.image-crop.double.right {
    background-position: right top;
    width: 240px;
    background-size: 960px auto;
}
</style>
